import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { accessoryService } from "../../../services/accessoryService";
import { API_BASE_URL } from "../../../config/api";

const AddAccessoryModal = ({ product, onClose, onSuccess }) => {
  const [codeInput, setCodeInput] = useState("");
  const [pendingCodes, setPendingCodes] = useState([]); // [{code, name, image}]
  const [suggestions, setSuggestions] = useState([]);
  const [allAccessoryProducts, setAllAccessoryProducts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savedAccessories, setSavedAccessories] = useState([]);
  const dropdownRef = useRef(null);

  /* --- Fetch existing accessories for this product --- */
  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const data = await accessoryService.getAccessoriesByProduct(product._id);
        setSavedAccessories(data);
      } catch (err) {
        console.error("Failed to load existing accessories:", err);
      }
    };
    fetchExisting();
  }, [product._id]);

  /* --- Fetch all products in Accessory category --- */
  useEffect(() => {
    const fetchAccessoryProducts = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/products/getallproducts`);
        const data = await res.json();
        const filtered = data.filter(
          (p) =>
            !p.isDeleted &&
            (p.categoryName?.toLowerCase() === "accessories" ||
              p.categoryName?.toLowerCase() === "accessory")
        );
        setAllAccessoryProducts(filtered);
      } catch (err) {
        console.error("Failed to fetch accessory products:", err);
      }
    };
    fetchAccessoryProducts();
  }, []);

  /* --- Autocomplete --- */
  const handleCodeInputChange = (e) => {
    const val = e.target.value;
    setCodeInput(val);
    setCodeError("");

    if (val.trim().length > 0) {
      const filtered = allAccessoryProducts.filter(
        (p) =>
          p.productCode?.toLowerCase().includes(val.toLowerCase()) ||
          p.productName?.toLowerCase().includes(val.toLowerCase())
      );
      setSuggestions(filtered);
      setShowDropdown(true);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const handleSelectSuggestion = (accessoryProduct) => {
    if (pendingCodes.find((p) => p.code === accessoryProduct.productCode)) {
      setCodeError(`"${accessoryProduct.productCode}" already in queue`);
      setShowDropdown(false);
      setCodeInput("");
      return;
    }
    if (
      savedAccessories.find(
        (a) => a.linkedProductCode === accessoryProduct.productCode
      )
    ) {
      setCodeError(
        `"${accessoryProduct.productCode}" is already linked as an accessory`
      );
      setShowDropdown(false);
      setCodeInput("");
      return;
    }
    setPendingCodes((prev) => [
      ...prev,
      {
        code: accessoryProduct.productCode,
        name: accessoryProduct.productName,
        image: accessoryProduct.images?.[0] || null,
      },
    ]);
    setCodeInput("");
    setSuggestions([]);
    setShowDropdown(false);
    setCodeError("");
  };

  const handleAddByCode = () => {
    const code = codeInput.trim();
    if (!code) return;
    const found = allAccessoryProducts.find(
      (p) => p.productCode?.toLowerCase() === code.toLowerCase()
    );
    if (!found) {
      setCodeError(
        `Product code "${code}" not found in the Accessory category`
      );
      return;
    }
    handleSelectSuggestion(found);
  };

  const removePending = (code) => {
    setPendingCodes((prev) => prev.filter((p) => p.code !== code));
  };

  /* --- Close dropdown on outside click --- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* --- Save all pending --- */
  const handleSaveAll = async () => {
    if (pendingCodes.length === 0) return;
    setUploading(true);
    let saved = 0;
    let failed = 0;
    for (const item of pendingCodes) {
      try {
        const formData = new FormData();
        formData.append("productId", product._id);
        formData.append("linkedProductCode", item.code);
        await accessoryService.createAccessory(formData);
        saved++;
      } catch (err) {
        console.error(`Failed to save ${item.code}:`, err);
        failed++;
      }
    }
    setUploading(false);
    if (failed > 0) {
      alert(`${saved} saved, ${failed} failed.`);
    } else {
      onSuccess?.();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-[520px] max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Add Accessories
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              For:{" "}
              <span className="font-medium text-purple-600">
                {product.productName}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Already saved */}
        {savedAccessories.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs font-semibold text-gray-600 mb-2">
              Already linked ({savedAccessories.length}):
            </p>
            <div className="flex flex-wrap gap-2">
              {savedAccessories.map((acc) => (
                <div
                  key={acc._id}
                  className="flex items-center gap-1.5 bg-white border rounded-full px-2 py-1"
                >
                  {acc.images?.[0] && (
                    <img
                      src={acc.images[0]}
                      alt={acc.name}
                      className="w-5 h-5 object-cover rounded-full"
                    />
                  )}
                  <span className="text-xs text-gray-700">
                    {acc.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info banner */}
        <div className="mb-4 flex items-start gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
          <svg
            className="w-4 h-4 text-purple-500 mt-0.5 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z"
            />
          </svg>
          <p className="text-xs text-purple-700">
            Only products in the{" "}
            <span className="font-semibold">Accessory</span> category can be
            added. Search by product code or name.
          </p>
        </div>

        {/* Autocomplete input */}
        <div className="relative mb-2" ref={dropdownRef}>
          <div className="flex gap-2">
            <input
              type="text"
              value={codeInput}
              onChange={handleCodeInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (suggestions.length === 1) {
                    handleSelectSuggestion(suggestions[0]);
                  } else {
                    handleAddByCode();
                  }
                }
              }}
              placeholder="Search by product code or name..."
              className="flex-1 border px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <button
              type="button"
              onClick={handleAddByCode}
              className="px-4 py-2 bg-purple-100 text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-200 text-sm font-medium"
            >
              Add
            </button>
          </div>

          {/* Dropdown */}
          {showDropdown && suggestions.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-xl max-h-48 overflow-y-auto">
              {suggestions.map((p) => (
                <button
                  key={p._id}
                  onMouseDown={() => handleSelectSuggestion(p)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-purple-50 text-left border-b last:border-0"
                >
                  {p.images?.[0] ? (
                    <img
                      src={p.images[0]}
                      alt={p.productName}
                      className="w-9 h-9 object-cover rounded"
                    />
                  ) : (
                    <div className="w-9 h-9 bg-gray-100 rounded flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No img</span>
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      {p.productName}
                    </div>
                    <div className="text-xs text-gray-400">
                      Code: {p.productCode}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {showDropdown && suggestions.length === 0 && codeInput.trim() && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-xl px-3 py-2 text-sm text-red-500">
              No Accessory products match "{codeInput}"
            </div>
          )}
        </div>

        {codeError && (
          <p className="text-xs text-red-500 mt-1 mb-2">{codeError}</p>
        )}

        {/* Pending chips */}
        {pendingCodes.length > 0 && (
          <div className="mt-3 mb-4">
            <p className="text-xs text-gray-600 mb-2 font-medium">
              Queued to save ({pendingCodes.length}):
            </p>
            <div className="flex flex-wrap gap-2">
              {pendingCodes.map((item) => (
                <div
                  key={item.code}
                  className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full px-3 py-1"
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-5 h-5 object-cover rounded-full"
                    />
                  )}
                  <span className="text-xs font-medium text-purple-700">
                    {item.name}
                  </span>
                  <span className="text-xs text-gray-400">({item.code})</span>
                  <button
                    onClick={() => removePending(item.code)}
                    className="text-red-400 hover:text-red-600 ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex justify-end gap-3 mt-5 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAll}
            disabled={uploading || pendingCodes.length === 0}
            className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
          >
            {uploading
              ? "Saving..."
              : pendingCodes.length > 0
              ? `Save ${pendingCodes.length} Accessor${pendingCodes.length > 1 ? "ies" : "y"}`
              : "Save Accessory"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAccessoryModal;
